/** Loads a lesson's content (cache-first) and derives the learner's position in its plan. */
import { useEffect, useMemo, useState } from 'react';
import { loadLesson, peekLesson } from '../data/lessons.ts';
import type { LessonContent, LessonMeta } from '../data/api.ts';
import { buildPlan, emptyStepsState, nextPosition, remainingMinutes, type ChapterPlan, type StepSpec } from '../core/lesson-plan.ts';
import { useLoadedData } from './app-data.tsx';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; content: LessonContent };

function cachedState(lessonId: string | null, knownRevision: string | null): LoadState {
  const cached = lessonId ? peekLesson(lessonId, knownRevision) : null;
  return cached ? { status: 'ready', content: cached } : { status: 'loading' };
}

export function useLessonContent(lessonId: string | null, knownRevision: string | null): LoadState {
  // A current cached copy renders on the first frame, without a loading flash.
  const [state, setState] = useState<LoadState>(() => cachedState(lessonId, knownRevision));
  useEffect(() => {
    if (!lessonId) return;
    let active = true;
    const cached = cachedState(lessonId, knownRevision);
    setState((s) =>
      cached.status === 'ready' ? (s.status === 'ready' && s.content === cached.content ? s : cached) : { status: 'loading' },
    );
    if (cached.status === 'ready') return;
    loadLesson(lessonId, knownRevision)
      .then((content) => active && setState({ status: 'ready', content }))
      .catch((e: unknown) =>
        active && setState({ status: 'error', message: e instanceof Error ? e.message : 'Не удалось загрузить урок' }),
      );
    return () => {
      active = false;
    };
  }, [lessonId, knownRevision]);
  return state;
}

export interface LessonPositionInfo {
  plan: ChapterPlan[];
  chapter: ChapterPlan;
  chapterIndex: number;
  stepIndex: number;
  minutes: number;
  stepKind: StepSpec['kind'];
}

export function stepKindLabel(kind: StepSpec['kind']): string {
  switch (kind) {
    case 'learn':
      return 'новые слова';
    case 'read':
      return 'чтение';
    case 'grammar':
      return 'грамматика';
    case 'question':
      return 'вопрос по тексту';
    case 'open':
      return 'практика';
    case 'exercise':
      return 'упражнения';
    case 'task':
      return 'задание';
  }
}

/** Where the learner stands in `meta` (null while loading or when nothing is left). */
export function useLessonPosition(meta: LessonMeta | null): LessonPositionInfo | null {
  const { progress } = useLoadedData();
  const loaded = useLessonContent(meta?.available ? meta.id : null, meta?.revision ?? null);
  return useMemo(() => {
    if (!meta || loaded.status !== 'ready') return null;
    const plan = buildPlan(loaded.content.lesson);
    const state = progress[meta.id]?.steps ?? emptyStepsState();
    const position = nextPosition(plan, state);
    if (!position) return null;
    const chapter = plan[position.chapterIndex]!;
    return {
      plan,
      chapter,
      chapterIndex: position.chapterIndex,
      stepIndex: position.stepIndex,
      minutes: remainingMinutes(chapter, state),
      stepKind: chapter.steps[position.stepIndex]!.spec.kind,
    };
  }, [meta, loaded, progress]);
}
